if (!customElements.get('product-sticky-bar')) {
  const STICKY_BAR_SCROLL_OFFSET = 300

  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId
        this.mobileQuery = window.matchMedia('(max-width: 749px)')

        this.stickyQuantityInput = this.querySelector('.product-sticky-bar__quantity-input')
        this.stickySubmitButton = this.querySelector('.product-sticky-bar__submit')
        this.stickySubmitText = this.stickySubmitButton?.querySelector('span')
        this.stickySpinner = this.stickySubmitButton?.querySelector('.loading__spinner')

        this.observer = null
        this.submitObserver = null
        this.isSyncingQuantity = false
        this.hasSeenBuyButtons = false

        this.onStickyQuantityChange = this.handleStickyQuantityChange.bind(this)
        this.onMainQuantityChange = this.handleMainQuantityChange.bind(this)
        this.onStickySubmit = this.handleSubmit.bind(this)
        this.onBreakpointChange = this.handleBreakpointChange.bind(this)
        this.onVariantChange = this.handleVariantChange.bind(this)

        this.stickyQuantityInput?.addEventListener('change', this.onStickyQuantityChange)
        this.stickySubmitButton?.addEventListener('click', this.onStickySubmit)
        this.mobileQuery.addEventListener('change', this.onBreakpointChange)

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          )
        }

        this.refreshTargets()
        this.handleBreakpointChange()
      }

      disconnectedCallback() {
        this.stopObserver()
        this.submitObserver?.disconnect()
        this.stickyQuantityInput?.removeEventListener('change', this.onStickyQuantityChange)
        this.mainQuantityInput?.removeEventListener('change', this.onMainQuantityChange)
        this.stickySubmitButton?.removeEventListener('click', this.onStickySubmit)
        this.mobileQuery.removeEventListener('change', this.onBreakpointChange)
        this.variantChangeUnsubscriber?.()
      }

      refreshTargets() {
        this.buyButtonsTarget = document.getElementById(
          `ProductBuyButtons-${this.sectionId}`
        )
        this.mainSubmitButton = document.getElementById(
          `ProductSubmitButton-${this.sectionId}`
        )

        if (this.mainQuantityInput) {
          this.mainQuantityInput.removeEventListener(
            'change',
            this.onMainQuantityChange
          )
        }

        this.mainQuantityInput = document.getElementById(
          `Quantity-${this.sectionId}`
        )
        this.mainQuantityInput?.addEventListener('change', this.onMainQuantityChange)

        this.bindSubmitObserver()

        if (!this.buyButtonsTarget || !this.mainSubmitButton) {
          this.hidden = true
          return false
        }

        this.hidden = false
        this.syncSubmitButton()
        this.syncMainToSticky()
        return true
      }

      bindSubmitObserver() {
        this.submitObserver?.disconnect()

        if (!this.mainSubmitButton || !this.stickySubmitButton) return

        this.submitObserver = new MutationObserver(() => {
          this.syncSubmitButton()
        })

        this.submitObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
          subtree: true,
          characterData: true,
          childList: true,
        })
      }

      handleBreakpointChange() {
        if (this.mobileQuery.matches && this.refreshTargets()) {
          this.startObserver()
          return
        }

        this.stopObserver()
        this.setBarVisible(false)
      }

      startObserver() {
        if (!this.buyButtonsTarget) return

        this.stopObserver()

        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              this.hasSeenBuyButtons = true
              this.setBarVisible(false)
              return
            }

            this.setBarVisible(this.hasSeenBuyButtons)
          },
          {
            threshold: 0,
            rootMargin: `-${STICKY_BAR_SCROLL_OFFSET}px 0px 0px 0px`,
          }
        )

        this.observer.observe(this.buyButtonsTarget)
      }

      stopObserver() {
        this.observer?.disconnect()
        this.observer = null
      }

      setBarVisible(visible) {
        this.classList.toggle('product-sticky-bar--visible', visible)
        this.toggleAttribute('aria-hidden', !visible)
      }

      handleStickyQuantityChange() {
        if (this.isSyncingQuantity || !this.mainQuantityInput || !this.stickyQuantityInput) {
          return
        }

        this.isSyncingQuantity = true
        this.mainQuantityInput.value = this.stickyQuantityInput.value
        this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }))
        this.isSyncingQuantity = false
      }

      handleMainQuantityChange() {
        if (this.isSyncingQuantity) return
        this.syncMainToSticky()
      }

      syncMainToSticky() {
        if (!this.mainQuantityInput || !this.stickyQuantityInput) return

        this.isSyncingQuantity = true
        this.stickyQuantityInput.value = this.mainQuantityInput.value

        ;['min', 'max', 'step', 'data-min', 'data-max'].forEach((attr) => {
          const value = this.mainQuantityInput.getAttribute(attr)
          if (value === null) {
            this.stickyQuantityInput.removeAttribute(attr)
          } else {
            this.stickyQuantityInput.setAttribute(attr, value)
          }
        })

        this.stickyQuantityInput
          .closest('quantity-input')
          ?.validateQtyRules?.()

        this.isSyncingQuantity = false
      }

      handleSubmit(event) {
        event.preventDefault()

        if (
          !this.mainSubmitButton ||
          this.mainSubmitButton.hasAttribute('disabled') ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return
        }

        this.handleStickyQuantityChange()
        this.mainSubmitButton.click()
      }

      handleVariantChange(event) {
        if (String(event?.data?.sectionId) !== String(this.sectionId)) return

        requestAnimationFrame(() => {
          this.refreshTargets()
          if (this.mobileQuery.matches) {
            this.startObserver()
          }
        })
      }

      syncSubmitButton() {
        if (!this.mainSubmitButton || !this.stickySubmitButton) return

        const mainText = this.mainSubmitButton.querySelector('span')?.textContent?.trim()
        const isDisabled =
          this.mainSubmitButton.hasAttribute('disabled') ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true'
        const isLoading = this.mainSubmitButton.classList.contains('loading')

        this.stickySubmitButton.toggleAttribute('disabled', isDisabled)
        this.stickySubmitButton.classList.toggle('loading', isLoading)

        if (this.stickySubmitText && mainText) {
          this.stickySubmitText.textContent = mainText
        }

        this.stickySpinner?.classList.toggle('hidden', !isLoading)
      }
    }
  )
}
